/** @format */

import { useNavigate, useParams } from "react-router-dom";
import type { ParentGroupSummary, SearchParams } from "@question-bank/shared";
import { formatSeason, toStringArray } from "../utils/helpers";
import { Locale } from "../features/i18n/init";

interface AdminQuestionTableProps {
	questions: ParentGroupSummary[];
	total: number;
	limit: number;
	offset: number;
	isLoading: boolean;
	error: string | null;
	filters: SearchParams;
	onPageChange: (offset: number) => void;
	onRetry: () => void;
}

const formatSeries = (series?: ParentGroupSummary["series"]): string => {
	if (!series) {
		return "-";
	}
	const season = formatSeason(series.season);
	return [season, series.year].filter(Boolean).join(", ") || "-";
};

const formatList = (values?: string[]): string => {
	if (!values || values.length === 0) {
		return "-";
	}
	return values.join(", ");
};

const StatusBadge = ({ status }: { status: ParentGroupSummary["status"] }) => {
	const STATUS_CLASSES: Record<ParentGroupSummary["status"], string> = {
		draft: "bg-amber-100 text-amber-800",
		staged: "bg-blue-100 text-blue-800",
		published: "bg-green-100 text-green-800",
	};
	// Legacy rows written before status existed arrive without one; show them as draft, as before.
	const classes = STATUS_CLASSES[status] ?? STATUS_CLASSES.draft;
	return (
		<span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold capitalize ${classes}`}>{status}</span>
	);
};

const SKELETON_ROW_COUNT = 5;
const TABLE_COLUMN_COUNT = 9;

const TableSkeleton = () => (
	<>
		{Array.from({ length: SKELETON_ROW_COUNT }).map((_, rowIdx) => (
			<tr key={rowIdx} className="animate-pulse">
				{Array.from({ length: TABLE_COLUMN_COUNT }).map((_, colIdx) => (
					<td key={colIdx} className="px-4 py-3">
						<div className="h-4 rounded bg-slate-200" />
					</td>
				))}
			</tr>
		))}
	</>
);

export const AdminQuestionTable = ({
	questions,
	total,
	limit,
	offset,
	isLoading,
	error,
	filters,
	onPageChange,
	onRetry,
}: AdminQuestionTableProps) => {
	const navigate = useNavigate();
	const { locale } = useParams<{ locale: Locale }>();
	const currentPage = Math.floor(offset / limit) + 1;
	const totalPages = Math.ceil(total / limit);
	const from = total === 0 ? 0 : offset + 1;
	const to = Math.min(offset + limit, total);

	if (error) {
		return (
			<div
				role="alert"
				className="rounded-lg border border-red-200 bg-red-50 p-6 text-center shadow-(--admin-shadow-raised)"
			>
				<p className="text-sm font-semibold text-red-700"> Failed to load questions </p>
				<p className="mt-1 text-sm text-red-600">{error}</p>
				<button
					type="button"
					onClick={onRetry}
					className="mt-4 rounded-md bg-red-700 px-4 py-2 text-sm font-semibold text-white hover:bg-red-800"
				>
					Try again
				</button>
			</div>
		);
	}

	return (
		<div className=" rounded-lg border border-slate-200 bg-white shadow-(--admin-shadow-raised)">
			<div className="border-b border-slate-100 px-4 py-3">
				{isLoading ? (
					<div className="h-40 w-40 animate-pulse rounded bg-slate-200" aria-label="Loading results" />
				) : (
					<p className="text-sm text-slate-600">
						{total === 0 ? (
							"No questions found"
						) : (
							<>
								Showing{" "}
								<span className="font-semibold">
									{from}-{to}
								</span>{" "}
								of <span className="font-semibold">{total.toLocaleString()} </span> questions
							</>
						)}
					</p>
				)}
			</div>

			<div className="overflow-x-auto">
				<table className="w-full test-sm">
					<thead>
						<tr className="border-b border-slate-100 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
							<th scope="col" className="w-36 px-4 py-3">
								Subject{" "}
							</th>
							<th scope="col" className="px-3 py-2">
								Level{" "}
							</th>
							<th scope="col" className="px-3 py-2 whitespace-nowrap">
								Q No.{" "}
							</th>
							<th scope="col" className="px-3 py-2 whitespace-nowrap">
								Paper Code{" "}
							</th>
							<th scope="col" className="px-3 py-2">
								Marks{" "}
							</th>
							<th scope="col" className="px-3 py-2">
								Parts{" "}
							</th>
							<th scope="col" className="px-3 py-2">
								Series{" "}
							</th>
							<th scope="col" className="px-3 py-2">
								Status{" "}
							</th>
						</tr>
					</thead>
					<tbody className="divide-y divide-slate-100">
						{isLoading ? (
							<TableSkeleton />
						) : questions.length === 0 ? (
							<tr>
								<td colSpan={TABLE_COLUMN_COUNT} className="px-4 py-12 text-center text-sm text-slate-500">
									No questions match the selected filters.
								</td>
							</tr>
						) : (
							questions.map((q) => {
								const viewPath = `/${locale}/admin/questions/${encodeURIComponent(q.parentQuestionId)}`;
								return (
									<tr
										key={q.parentQuestionId}
										onClick={() => navigate(viewPath, { state: { filters } })}
										className="cursor-pointer transition-colors hover:bg-slate-50"
									>
										<td
											className="w-36 max-w-36 truncate px-4 py-3 capitalize text-slate-700"
											title={formatList(toStringArray(q.subject))}
										>
											{formatList(toStringArray(q.subject))}
										</td>
										<td className="px-4 py-3 text-slate-700" title={q.qualification.title ?? q.qualification.code}>
											{q.qualification.title ?? q.qualification.code}
										</td>
										<td className="px-4 py-3 text-slate-700">{q.questionNumber}</td>
										<td className="px-4 py-3 text-slate-700">{q.paperCode ?? "-"}</td>
										<td className="px-4 py-3 text-slate-700">{q.totalMarks}</td>
										<td className="px-4 py-3 text-slate-700">{q.childCount}</td>
										<td className="px-4 py-3 whitespace-nowrap text-slate-700">{formatSeries(q.series)}</td>
										<td className="px-4 py-3">
											<StatusBadge status={q.status} />
										</td>
									</tr>
								);
							})
						)}
					</tbody>
				</table>
				{total > limit && (
					<div className="flex items-center justify-between border-t border-slate-100 px-4 py-3">
						<p className="text-xs text-slate-500">
							Page {currentPage} of {totalPages}
						</p>
						<div className="flex gap-2">
							<button
								type="button"
								disabled={offset === 0}
								onClick={() => onPageChange(Math.max(0, offset - limit))}
								className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
							>
								Previous
							</button>
							<button
								type="button"
								disabled={offset + limit >= total}
								onClick={() => onPageChange(offset + limit)}
								className="rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
							>
								Next
							</button>
						</div>
					</div>
				)}
			</div>
		</div>
	);
};
